import { Component, Inject, OnDestroy, OnInit, Optional, SecurityContext } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FilterOptions, ISColumn, ISMultiSelectDatasource, ISMultiSelectOption, ISPagination, ISSelectOption } from 'is-lib';
import { BehaviorSubject, catchError, finalize, forkJoin, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { supportedFileUploadTypes } from 'src/app/constants';
import { ServerResponse } from 'src/app/models/ServerResponse';
import { SnackBarType } from 'src/app/models/Snackbar';
import { CreateTicket, CreateTicketMessage, TicketTyping } from 'src/app/models/Ticket';
import { IContractElectricitySupply } from 'src/app/models/ElectricitySupply';
import { IContractNaturalGasSupply } from 'src/app/models/NaturalGasSupply';
import { ContractsService } from 'src/app/services/contracts.service';
import { SnackBarService } from 'src/app/services/snackbar.service';
import { TicketsService } from 'src/app/services/tickets.service';
import { UsersService } from 'src/app/services/users.service';
import { environment } from 'src/environments/environment';

export enum TicketFormStep {
    Category = 0,
    Subcategory = 1,
    Details = 2
}

export enum TicketAssociationType {
    Contract = 0,
    Cpe = 1,
    Cui = 2,
    Entity = 3
}

/** Data passed when the form is opened as a dialog (e.g. from the contracts list) */
export interface TicketFormDialogData {
    contracts: { id: string, number: string }[];
    electricitySupplies: { id: string, code: string }[];
    naturalGasSupplies: { id: string, code: string }[];
}

/** Contract as emitted by the contracts multi-select (description is the contract number) */
interface SelectedContract {
    id: string;
    description: string;
}

/** One ticket to create: what's shown in the confirmation dialog and what the ticket gets associated with */
export interface TicketToCreate {
    code?: string;
    contractNumber?: string;
    targets: AssociationTargets;
}

interface ContractSupplies {
    electricity: IContractElectricitySupply[];
    naturalGas: IContractNaturalGasSupply[];
}

export interface AssociationTargets {
    contractIds: string[];
    electricitySupplyIds: string[];
    naturalGasSupplyIds: string[];
    entityIds: string[];
}

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const ASSOCIATED_GROUP_NAME = 'SIGA_MV Departamento Comercial';
const ATTACHMENT_FILE_TYPE_ID = '1';

@Component({
    selector: 'app-ticket-form',
    templateUrl: './ticket-form.component.html',
    styleUrls: ['./ticket-form.component.scss']
})
export class TicketFormComponent implements OnInit, OnDestroy {
    readonly Step = TicketFormStep;
    readonly AssociationType = TicketAssociationType;
    readonly supportedFileTypes = supportedFileUploadTypes;
    readonly supportedFileTypesLabel = supportedFileUploadTypes.map(type => type.replace('.', '').toUpperCase()).join(', ');

    readonly steps = [
        { step: TicketFormStep.Category, label: 'CATEGORY' },
        { step: TicketFormStep.Subcategory, label: 'SUBCATEGORY' },
        { step: TicketFormStep.Details, label: 'REQUEST_IDENTIFIER' }
    ];

    readonly associationTypeOptions: ISSelectOption[] = [
        { value: +TicketAssociationType.Contract, label: 'CONTRACT' },
        { value: +TicketAssociationType.Cpe, label: 'CPE' },
        { value: +TicketAssociationType.Cui, label: 'CUI' },
        { value: +TicketAssociationType.Entity, label: 'ENTITY' }
    ];

    readonly contractsDatasource = new BehaviorSubject<ISMultiSelectDatasource>({
        apiUrl: `${environment.apiUrl}ClientPortalUsers/${this._user.userCountryCode}/Mine/Contracts/Get`,
        columns: [
            new ISColumn('guidId', 'guidId', new FilterOptions(null, undefined, undefined, undefined, 'guidId')),
            new ISColumn('contractNumber', 'contractNumber', new FilterOptions(null, undefined, undefined, undefined, 'contractNumber'))
        ],
        pagination: new ISPagination(25, 0),
        filters: [],
        totalItems: 0
    });

    cpeOptions: ISMultiSelectOption[] = [];
    cuiOptions: ISMultiSelectOption[] = [];
    suppliesLoading = false;

    readonly ticketForm = new FormGroup({
        associationType: new FormControl<number | null>(null),
        contract: new FormControl<string[] | null>(null),
        cpe: new FormControl<string[] | null>(null),
        cui: new FormControl<string[] | null>(null),
        description: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        files: new FormControl<File[]>([], { nonNullable: true })
    });

    ticketTypes: TicketTyping[] = [];
    ticketSubtypes: TicketTyping[] = [];

    currentStep = TicketFormStep.Category;
    selectedTypeId?: number;
    selectedSubtypeId?: number;
    isDragOver = false;
    isSubmitting = false;

    private _subtypesLoadedForTypeId?: number;
    private _selectedContracts: SelectedContract[] = [];
    private readonly _selectedContracts$ = new Subject<SelectedContract[]>();
    /** Supplies already fetched, by contract id, so a contract's supplies are only requested the first time it's selected */
    private readonly _suppliesByContractId = new Map<string, ContractSupplies>();
    private readonly _destroy$ = new Subject<void>();

    constructor(
        private _snackbarService: SnackBarService,
        private _dialog: MatDialog,
        private _ticketService: TicketsService,
        private _contractsService: ContractsService,
        private _user: UsersService,
        private _router: Router,
        @Optional() private _dialogRef: MatDialogRef<TicketFormComponent> | null,
        @Optional() @Inject(MAT_DIALOG_DATA) readonly dialogData: TicketFormDialogData | null
    ) { }

    get isDialog(): boolean {
        return this.dialogData != null;
    }

    get files(): File[] {
        return this.ticketForm.controls.files.value;
    }

    // is-select emits the value as a string, so it's converted back to the enum's number for strict comparisons
    get associationType(): TicketAssociationType | null {
        const value = this.ticketForm.controls.associationType.value;
        return String.isNullOrUndefinedOrWhitespace(value as any) ? null : +value!;
    }

    get canContinue(): boolean {
        switch (this.currentStep) {
            case TicketFormStep.Category: return this.selectedTypeId != null;
            case TicketFormStep.Subcategory: return this.selectedSubtypeId != null;
            case TicketFormStep.Details: return !this.isSubmitting;
        }
    }

    ngOnInit(): void {
        this.loadTicketTypes();

        if (this.dialogData) {
            this._applyDialogData(this.dialogData);
        } else {
            this.ticketForm.controls.associationType.addValidators(Validators.required);
            this.ticketForm.controls.associationType.valueChanges
                .pipe(takeUntil(this._destroy$))
                .subscribe(type => this._onAssociationTypeChanged(type?.toString() ?? null));

            // switchMap drops the responses of a previous contract selection that are still in flight
            this._selectedContracts$
                .pipe(
                    switchMap(contracts => this._getContractsSupplies(contracts)),
                    takeUntil(this._destroy$)
                )
                .subscribe(({ cpes, cuis }) => {
                    this.cpeOptions = cpes;
                    this.cuiOptions = cuis;
                    this.suppliesLoading = false;
                });
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    //#region Navigation
    selectType(typeId: number): void {
        if (this.selectedTypeId !== typeId) {
            this.selectedTypeId = typeId;
            this.selectedSubtypeId = undefined;
        }
    }

    goToStep(step: TicketFormStep): void {
        if (step < this.currentStep) {
            this.currentStep = step;
        }
    }

    previousStep(): void {
        if (this.currentStep > TicketFormStep.Category) {
            this.currentStep--;
        }
    }

    nextStep(): void {
        if (!this.canContinue) {
            return;
        }

        switch (this.currentStep) {
            case TicketFormStep.Category:
                this.loadTicketSubtypes(this.selectedTypeId!);
                this.currentStep = TicketFormStep.Subcategory;
                break;
            case TicketFormStep.Subcategory:
                this.currentStep = TicketFormStep.Details;
                break;
            case TicketFormStep.Details:
                this.submit();
                break;
        }
    }

    close(created: boolean = false): void {
        this._dialogRef?.close(created);
    }
    //#endregion

    //#region Association
    /** The multi-select emits before updating its form control, so the contracts are read from the event */
    onContractsChanged(selected: SelectedContract[]): void {
        this.ticketForm.patchValue({ cpe: null, cui: null }, { emitEvent: false });

        this.suppliesLoading = selected.some(contract => !this._suppliesByContractId.has(contract.id));
        this._selectedContracts = selected;
        this._selectedContracts$.next(selected);
    }

    private _onAssociationTypeChanged(value: string | null): void {
        const { contract, cpe, cui } = this.ticketForm.controls;
        const type: TicketAssociationType | null = String.isNullOrUndefinedOrWhitespace(value) ? null : +value!;

        cpe.reset(null);
        cui.reset(null);
        if (type == null || type === TicketAssociationType.Entity) {
            contract.reset(null);
        }

        contract.setValidators(type != null && type !== TicketAssociationType.Entity ? Validators.required : null);
        cpe.setValidators(type === TicketAssociationType.Cpe ? Validators.required : null);
        cui.setValidators(type === TicketAssociationType.Cui ? Validators.required : null);

        [contract, cpe, cui].forEach(control => control.updateValueAndValidity({ emitEvent: false }));
    }

    /**
     * Supplies are fetched per contract, so one request is made for each newly selected contract
     * (the ones already fetched come from the cache) and the results of all selected contracts are joined
     */
    private _getContractsSupplies(contracts: SelectedContract[]): Observable<{ cpes: ISMultiSelectOption[], cuis: ISMultiSelectOption[] }> {
        const newContracts = contracts.filter(contract => !this._suppliesByContractId.has(contract.id));
        const requests = newContracts.map(contract => this._fetchContractSupplies(contract.id));

        return (requests.length > 0 ? forkJoin(requests) : of([])).pipe(
            map(() => {
                const supplies = contracts.map(contract => this._suppliesByContractId.get(contract.id));

                return {
                    cpes: this._toGroupedSupplyOptions(contracts, supplies.map(s => s?.electricity ?? []), supply => supply.electricityMeterCode),
                    cuis: this._toGroupedSupplyOptions(contracts, supplies.map(s => s?.naturalGas ?? []), supply => supply.naturalGasMeterCode)
                };
            })
        );
    }

    /** Fetches a contract's supplies into the cache. A failed request isn't cached (so it's retried on the next selection) and only loses that contract's supplies */
    private _fetchContractSupplies(contractId: string): Observable<void> {
        return forkJoin({
            electricity: this._contractsService.getContractElectricitySupplies(contractId).pipe(map(sr => sr.data ?? [])),
            naturalGas: this._contractsService.getContractNaturalGasSupplies(contractId).pipe(map(sr => sr.data ?? []))
        }).pipe(
            map(supplies => { this._suppliesByContractId.set(contractId, supplies); }),
            catchError(() => of(undefined))
        );
    }

    /**
     * Groups the supplies under the contract they were fetched from (contracts without supplies are left out).
     * The same supply can belong to more than one of the selected contracts, so it's only kept under the first one
     */
    private _toGroupedSupplyOptions<T extends { guidId: string }>(contracts: SelectedContract[], suppliesByContract: T[][], getCode: (supply: T) => string): ISMultiSelectOption[] {
        const addedSupplyIds = new Set<string>();

        return contracts
            .map((contract, i) => {
                const children = suppliesByContract[i]
                    .filter(supply => !addedSupplyIds.has(supply.guidId))
                    .map(supply => {
                        addedSupplyIds.add(supply.guidId);
                        return new ISMultiSelectOption(supply.guidId, getCode(supply));
                    });

                return new ISMultiSelectOption(contract.id, contract.description, undefined, undefined, children);
            })
            .filter(option => option.children!.length > 0);
    }

    private _applyDialogData(data: TicketFormDialogData): void {
        this.ticketForm.patchValue({
            contract: data.contracts.map(contract => contract.id),
            cpe: data.electricitySupplies.map(supply => supply.id),
            cui: data.naturalGasSupplies.map(supply => supply.id)
        });
    }

    /**
     * Each ticket is only associated with what the association type refers to: a ticket for each selected contract, CPE or CUI,
     * or a single ticket for the entity (the contract shown for a CPE/CUI is only informative in the confirmation dialog).
     * When opened as a dialog a single ticket is created with every association passed to it
     */
    private _getTicketsToCreate(): TicketToCreate[] {
        const emptyTargets: AssociationTargets = { contractIds: [], electricitySupplyIds: [], naturalGasSupplyIds: [], entityIds: [] };
        const { contract, cpe, cui } = this.ticketForm.getRawValue();

        if (this.isDialog) {
            return [{ targets: { ...emptyTargets, contractIds: contract ?? [], electricitySupplyIds: cpe ?? [], naturalGasSupplyIds: cui ?? [] } }];
        }

        switch (this.associationType) {
            case TicketAssociationType.Contract:
                return (contract ?? []).map(contractId => ({
                    code: this._selectedContracts.find(c => c.id === contractId)?.description,
                    targets: { ...emptyTargets, contractIds: [contractId] }
                }));
            case TicketAssociationType.Cpe:
                return (cpe ?? []).map(supplyId => {
                    const { supply, contract } = this._findSupply(supplyId, supplies => supplies.electricity);
                    return {
                        code: supply?.electricityMeterCode,
                        contractNumber: contract?.description,
                        targets: { ...emptyTargets, electricitySupplyIds: [supplyId] }
                    };
                });
            case TicketAssociationType.Cui:
                return (cui ?? []).map(supplyId => {
                    const { supply, contract } = this._findSupply(supplyId, supplies => supplies.naturalGas);
                    return {
                        code: supply?.naturalGasMeterCode,
                        contractNumber: contract?.description,
                        targets: { ...emptyTargets, naturalGasSupplyIds: [supplyId] }
                    };
                });
            case TicketAssociationType.Entity: {
                const entityGuid = this._user.userData?.entityGuid;
                return [{ targets: { ...emptyTargets, entityIds: entityGuid ? [entityGuid] : [] } }];
            }
            default:
                return [];
        }
    }

    /** Finds a supply in the fetched supplies of the selected contracts (the first contract that has it, matching how the select groups them) */
    private _findSupply<T extends { guidId: string }>(supplyId: string, getSupplies: (supplies: ContractSupplies) => T[]): { supply?: T, contract?: SelectedContract } {
        for (const contract of this._selectedContracts) {
            const contractSupplies = this._suppliesByContractId.get(contract.id);
            const supply = contractSupplies ? getSupplies(contractSupplies).find(s => s.guidId === supplyId) : undefined;

            if (supply) {
                return { supply, contract };
            }
        }

        return {};
    }

    //#endregion

    //#region Files
    onFilesSelected(input: HTMLInputElement): void {
        this._addFiles(input.files);
        // Allows selecting the same file again after removing it
        input.value = '';
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(): void {
        this.isDragOver = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
        this._addFiles(event.dataTransfer?.files ?? null);
    }

    canPreviewFile(file: File): boolean {
        return file.type.startsWith('image/') || file.type === 'application/pdf';
    }

    viewFile(file: File): void {
        if (this.canPreviewFile(file)) {
            this._dialog.open(FileViewerComponent, { data: { dataType: file.type.startsWith('image/') ? 'image' : 'pdf', file } });
        }
    }

    removeFile(file: File): void {
        this.ticketForm.controls.files.setValue(this.files.filter(f => f !== file));
    }

    getFileIcon(file: File): string {
        const extension = this._getExtension(file.name);

        switch (extension) {
            case '.pdf': return 'fa-file-pdf';
            case '.doc': return 'fa-file-word';
            case '.xls': return 'fa-file-excel';
            case '.zip':
            case '.rar': return 'fa-file-zipper';
            default: return file.type.startsWith('image/') ? 'fa-file-image' : 'fa-file';
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes >= 1024 * 1024) {
            return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        }
        if (bytes >= 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${bytes} B`;
    }

    private _addFiles(fileList: FileList | null): void {
        if (!fileList || fileList.length === 0) {
            return;
        }

        const currentFiles = this.files;
        const newFiles = Array.from(fileList).filter(file => !currentFiles.some(existing => existing.name === file.name));
        const validFiles = newFiles.filter(file => this._isValidFile(file));
        const invalidFileCount = newFiles.length - validFiles.length;

        if (invalidFileCount > 0) {
            this._snackbarService.callSnackBar('INVALID_FILES_NOT_SAVED', SnackBarType.WARNING, undefined, undefined, undefined, { fileCount: invalidFileCount });
        }

        this.ticketForm.controls.files.setValue([...currentFiles, ...validFiles]);
    }

    private _isValidFile(file: File): boolean {
        const extension = this._getExtension(file.name);
        return extension != null && this.supportedFileTypes.includes(extension) && file.size <= MAX_FILE_SIZE_BYTES;
    }

    private _getExtension(fileName: string): string | null {
        const dotIndex = fileName.lastIndexOf('.');
        return dotIndex > 0 ? fileName.substring(dotIndex).toLowerCase() : null;
    }
    //#endregion

    //#region APIRequests
    loadTicketTypes(): void {
        this._ticketService.getTicketTypes()
            .pipe(takeUntil(this._destroy$))
            .subscribe(rs => this.ticketTypes = rs.data ?? []);
    }

    loadTicketSubtypes(ticketTypeId: number): void {
        if (this._subtypesLoadedForTypeId === ticketTypeId) {
            return;
        }

        this.ticketSubtypes = [];
        this._ticketService.getTicketTypeSubtypes(ticketTypeId)
            .pipe(takeUntil(this._destroy$))
            .subscribe(rs => {
                this.ticketSubtypes = rs.data ?? [];
                this._subtypesLoadedForTypeId = ticketTypeId;
            });
    }

    private submit(): void {
        this.ticketForm.markAllAsTouched();

        if (this.ticketForm.invalid || this.selectedSubtypeId == null) {
            return;
        }

        const user = this._user.userData!;
        const body: CreateTicket = {
            associatedGroupName: ASSOCIATED_GROUP_NAME,
            contactPersonEmail: user.email,
            contactPersonFirstName: user.firstName,
            contactPersonLastName: user.lastName,
            contactPersonTIN: user.tin,
            message: { body: this.ticketForm.controls.description.value },
            ticketSubtypeId: this.selectedSubtypeId
        };

        const tickets = this._getTicketsToCreate();
        const needsConfirmation = !this.isDialog && this.associationType != null && this.associationType !== TicketAssociationType.Entity;

        if (!needsConfirmation) {
            this._createTickets(body, tickets);
            return;
        }

        this._dialog.open(TicketCreationConfirmationComponent, {
            data: { associationType: this.associationType, tickets } as TicketCreationConfirmationData,
            autoFocus: false
        })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(confirmed => {
                if (confirmed) {
                    this._createTickets(body, tickets);
                }
            });
    }

    private _createTickets(body: CreateTicket, tickets: TicketToCreate[]): void {
        this.isSubmitting = true;

        forkJoin(tickets.map(ticket => this._createTicket(body, ticket.targets)))
            .pipe(
                finalize(() => this.isSubmitting = false),
                takeUntil(this._destroy$)
            )
            .subscribe({
                next: () => {
                    this._snackbarService.callSnackBar('SUPPORT_REQUEST_CREATED', SnackBarType.SUCCESS);

                    if (this.isDialog) {
                        this.close(true);
                    } else {
                        this._router.navigate(['/tickets/messages']);
                    }
                },
                error: () => this._snackbarService.callSnackBar('ERROR_OCURRED', SnackBarType.ERROR)
            });
    }

    /** Creates the ticket, then associates it with every target and posts the message with its attachments */
    private _createTicket(body: CreateTicket, targets: AssociationTargets): Observable<ServerResponse<any>[]> {
        return this._ticketService.postTicket(body).pipe(
            switchMap(ticketRs => {
                const ticketId = ticketRs.data.id;

                return forkJoin([
                    ...targets.contractIds.map(id => this._ticketService.associateTicketToContract(id, ticketId)),
                    ...targets.electricitySupplyIds.map(id => this._ticketService.associateTicketToElectricitySupplyContract(id, ticketId)),
                    ...targets.naturalGasSupplyIds.map(id => this._ticketService.associateTicketToNaturalGasSupplyContract(id, ticketId)),
                    ...targets.entityIds.map(id => this._ticketService.associateTicketToEntity(id, ticketId)),
                    this._postMessageWithFiles(ticketId, body.message)
                ]);
            })
        );
    }

    private _postMessageWithFiles(ticketId: number, message: CreateTicketMessage): Observable<ServerResponse<any>> {
        return this._ticketService.postTicketMessage(ticketId, message).pipe(
            switchMap(messageRs => {
                if (this.files.length === 0) {
                    return of(messageRs);
                }

                return forkJoin(this.files.map(file => {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('typeId', ATTACHMENT_FILE_TYPE_ID);

                    return this._ticketService.postTicketMessageFile(ticketId, messageRs.data, formData);
                })).pipe(map(() => messageRs));
            })
        );
    }
    //#endregion

    trackById(_: number, item: TicketTyping): number {
        return item.id;
    }

    trackByFileName(_: number, file: File): string {
        return file.name;
    }
}

@Component({
    template: `
        <div *ngIf="imageSrc != null">
            <img [src]="imageSrc" style="max-width: 90vw; max-height: 90vh">
        </div>
        <div *ngIf="fileSrc != null" style="max-height: 90vh; height: 90vh; aspect-ratio: 1/1; overflow: hidden">
            <iframe scrolling="no" style="height:100%; width: 100%; overflow: hidden" type="application/pdf" [src]="fileSrc"></iframe>
        </div>
    `
})
export class FileViewerComponent implements OnInit, OnDestroy {
    readonly imageFileTypes = ['image', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
    imageSrc: string | null = null;
    fileSrc: SafeResourceUrl | null = null;

    private _objectUrl?: string;

    constructor(
        @Inject(MAT_DIALOG_DATA) private readonly data: { dataType: string, file?: File, fileUrl?: string } | null,
        private _sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        if (!this.data) {
            return;
        }

        if (this.imageFileTypes.includes(this.data.dataType)) {
            if (this.data.file) {
                this._readImage(this.data.file);
            } else if (this.data.fileUrl) {
                this.imageSrc = this._sanitizer.sanitize(SecurityContext.URL, this.data.fileUrl);
            }
        } else if (this.data.dataType === 'pdf') {
            if (this.data.file) {
                this._objectUrl = URL.createObjectURL(this.data.file);
                this.fileSrc = this._sanitizer.bypassSecurityTrustResourceUrl(this._objectUrl);
            } else if (this.data.fileUrl) {
                this.fileSrc = this._sanitizer.bypassSecurityTrustResourceUrl(this.data.fileUrl);
            }
        }
    }

    ngOnDestroy(): void {
        if (this._objectUrl) {
            URL.revokeObjectURL(this._objectUrl);
        }
    }

    private _readImage(file: File): void {
        const fileReader = new FileReader();

        fileReader.onload = () => {
            const safeUrl = this._sanitizer.sanitize(SecurityContext.URL, fileReader.result?.toString() ?? null);
            this.imageSrc = String.isNullOrUndefinedOrWhitespace(safeUrl) ? null : safeUrl;
        };
        fileReader.onerror = fileReader.onabort = () => this.imageSrc = null;

        fileReader.readAsDataURL(file);
    }
}

export interface TicketCreationConfirmationData {
    associationType: TicketAssociationType;
    tickets: TicketToCreate[];
}

/** Tells the user that a ticket is created for each selected contract/CPE/CUI before submitting. Closes with true when confirmed */
@Component({
    template: `
        <div class="p-8" style="width: 480px; max-width: 90vw">
            <div class="d-flex align-items-center gap-3 mb-4">
                <i class="fa-duotone fa-solid fa-ticket fs-2x text-primary"></i>
                <h2 class="fs-3 fw-bold text-dark m-0">{{ 'TICKET_CREATION_CONFIRMATION_TITLE' | translate }}</h2>
            </div>

            <div class="fs-6 text-gray-700 mb-4">
                {{ 'TICKET_CREATION_CONFIRMATION_MESSAGE' | translate: { count: data.tickets.length, type: (associationTypeLabel | translate) } }}
            </div>

            <ul class="list-unstyled border rounded overflow-auto mb-6" style="max-height: 300px">
                <li *ngFor="let ticket of data.tickets; let last = last" class="d-flex align-items-center gap-3 px-4 py-3" [class.border-bottom]="!last">
                    <i class="fa-solid text-gray-500" [ngClass]="itemIcon"></i>
                    <div class="min-w-0">
                        <div class="fw-semibold text-gray-800 text-truncate">{{ ticket.code | empty: '-' }}</div>
                        <div *ngIf="ticket.contractNumber" class="fs-7 text-muted text-truncate">
                            {{ 'CONTRACT' | translate }}: {{ ticket.contractNumber }}
                        </div>
                    </div>
                </li>
            </ul>

            <div class="d-flex justify-content-end gap-3">
                <button type="button" class="btn btn-light" (click)="dialogRef.close(false)">{{ 'CANCEL' | translate }}</button>
                <button type="button" class="btn btn-primary" (click)="dialogRef.close(true)">
                    {{ 'SUBMIT' | translate }}<i class="fa-solid fa-paper-plane ms-2"></i>
                </button>
            </div>
        </div>
    `
})
export class TicketCreationConfirmationComponent {
    constructor(
        @Inject(MAT_DIALOG_DATA) readonly data: TicketCreationConfirmationData,
        readonly dialogRef: MatDialogRef<TicketCreationConfirmationComponent, boolean>
    ) { }

    get associationTypeLabel(): string {
        switch (this.data.associationType) {
            case TicketAssociationType.Cpe: return 'CPE';
            case TicketAssociationType.Cui: return 'CUI';
            default: return 'CONTRACT';
        }
    }

    get itemIcon(): string {
        switch (this.data.associationType) {
            case TicketAssociationType.Cpe: return 'fa-bolt';
            case TicketAssociationType.Cui: return 'fa-fire-flame-simple';
            default: return 'fa-file-contract';
        }
    }
}
